
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Brain, Sparkles, Loader2, AlertTriangle, MonitorPlay, Lock, Globe, X, Check, Infinity, List, Trash2, Plus, ArrowRightCircle, CheckCircle2, Grid3X3, ShieldCheck, Database, Radio, Gift } from 'lucide-react';
import { Survey, SurveyQuestionDraft, SurveyStatus, SurveyOption, QuestionConfig, LootTable, DigitalReward, RewardType, LocalizedText } from '../types';
import { AttributeDefinition, ATTRIBUTE_OPTIONS } from '../services/zkVault/types';
import { fetchAttributeDefinitions, fetchAttributeReach, AttributeFilter } from '../services/attributesService';
import { createSurvey, updateSurvey, fetchSurveyQuestions, getSurveyResponseCount, setKioskCode } from '../services/surveyService';
import { fetchLootTables, fetchDigitalRewards } from '../services/rewardService';
import { generateSurveySchema } from '../services/geminiService';

// Limit applies ONLY to AI generation to prevent hallucinations/spam. Manual creation is unlimited.
const AI_GENERATION_LIMIT = 15;

const ResearchFormView: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editingId = searchParams.get('edit');

  const [loading, setLoading] = useState(false);
  
  // Language & Content State
  const [activeLanguages, setActiveLanguages] = useState<string[]>(['en']);
  const [newLangCode, setNewLangCode] = useState('');
  
  const [title, setTitle] = useState<LocalizedText>({ en: '' });
  const [description, setDescription] = useState<LocalizedText>({ en: '' });
  
  // Reward Selection Logic
  const [rewardType, setRewardType] = useState<RewardType>('NONE'); 
  const [selectedRewardId, setSelectedRewardId] = useState<string>('');
  
  // Settings
  const [isKiosk, setIsKiosk] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const [memberBox, setMemberBox] = useState(false);
  const [requireLogin, setRequireLogin] = useState(false);
  // Kiosk access code (hashed server-side). The code is never returned on edit, so
  // an empty value means "keep the existing code"; a non-empty value sets/replaces it.
  const [kioskCode, setKioskCodeValue] = useState('');

  // DataFi: private data request + dedicated reward for sharing
  const [attrDefs, setAttrDefs] = useState<AttributeDefinition[]>([]);
  const [requestedAttributeIds, setRequestedAttributeIds] = useState<string[]>([]);
  const [dataRewardPoints, setDataRewardPoints] = useState<number | null>(null);
  // Creator-tunable reward for answering (points). Owners raise it to compete.
  const [completionReward, setCompletionReward] = useState<number>(1);
  // Audience targeting over value_summary (privacy-preserving reach estimate).
  const [targetValues, setTargetValues] = useState<Record<string, string>>({});
  const [reach, setReach] = useState<number | null>(null);
  const [reachLoading, setReachLoading] = useState(false);

  // Recompute reach whenever the targeting filters change.
  useEffect(() => {
    const filters: AttributeFilter[] = Object.entries(targetValues)
      .filter(([, v]) => v)
      .map(([attribute_id, value]) => ({ attribute_id, value }));
    if (filters.length === 0) { setReach(null); return; }
    let cancelled = false;
    setReachLoading(true);
    fetchAttributeReach(filters)
      .then((n) => { if (!cancelled) setReach(n); })
      .finally(() => { if (!cancelled) setReachLoading(false); });
    return () => { cancelled = true; };
  }, [targetValues]);
  
  // Available Options
  const [availDigiRewards, setAvailDigiRewards] = useState<DigitalReward[]>([]);
  const [availLootTables, setAvailLootTables] = useState<LootTable[]>([]);

  const [maxRes, setMaxRes] = useState<number | null>(null);
  const [status, setStatus] = useState<SurveyStatus>('DRAFT');
  
  const [questions, setQuestions] = useState<SurveyQuestionDraft[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  
  // AI Generation State
  const [isAiGenerating, setIsAiGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState('');

  // Lock State for existing responses
  const [existingResponseCount, setExistingResponseCount] = useState(0);
  const isQuestionLocked = existingResponseCount > 0;

  // Load data
  useEffect(() => {
    // 1. Fetch available rewards
    const fetchOptions = async () => {
        try {
            const [digi, loot, defs] = await Promise.all([fetchDigitalRewards(), fetchLootTables(), fetchAttributeDefinitions()]);
            setAvailDigiRewards(digi);
            setAvailLootTables(loot);
            setAttrDefs(defs);
        } catch (e) {
            console.error(e);
        }
    };
    fetchOptions();

    // 2. Fetch existing survey if editing
    if (editingId) {
        loadSurveyForEdit(editingId);
    }
  }, [editingId]);

  const loadSurveyForEdit = async (id: string) => {
    setLoading(true);
    try {
        // Fetch survey, questions, and response count in parallel
        const [surveyData, qs, count] = await Promise.all([
            import('../services/surveyService').then(m => m.fetchSurveyById(id)),
            fetchSurveyQuestions(id),
            getSurveyResponseCount(id)
        ]);

        setTitle(surveyData.title);
        setDescription(surveyData.description || {});
        setIsKiosk(!!surveyData.is_kiosk);
        setIsLive(!!surveyData.is_live);
        setMemberBox(!!surveyData.completion_box);
        setRequireLogin(!!surveyData.require_login);
        
        // Detect used languages
        const langs = new Set<string>();
        Object.keys(surveyData.title).forEach(k => langs.add(k));
        Object.keys(surveyData.description || {}).forEach(k => langs.add(k));
        
        // Ensure at least one language is active
        if (langs.size === 0) langs.add('en');
        setActiveLanguages(Array.from(langs));
        
        // Load Reward Config using distinct FKs
        if (surveyData.reward_type) {
            setRewardType(surveyData.reward_type);
            if (surveyData.reward_type === 'DIGITAL' && surveyData.digital_reward_id) {
                setSelectedRewardId(surveyData.digital_reward_id);
            } else if (surveyData.reward_type === 'PHYSICAL' && surveyData.loot_table_id) {
                setSelectedRewardId(surveyData.loot_table_id);
            }
        } 

        setMaxRes(surveyData.max_responses);
        setStatus(surveyData.status);
        setRequestedAttributeIds(surveyData.requested_attribute_ids || []);
        setDataRewardPoints(surveyData.data_reward_points ?? null);
        setCompletionReward(surveyData.completion_reward_points ?? 1);
        setTargetValues(
          Object.fromEntries((surveyData.target_attributes || []).map((t) => [t.attribute_id, t.value])),
        );
        setQuestions(qs);
        setExistingResponseCount(count);

    } catch (e) {
        console.error("Failed to load survey", e);
        alert("Could not load survey details.");
        navigate('/research/campaigns');
    } finally {
        setLoading(false);
    }
  };

  const addLanguage = () => {
      const code = newLangCode.toLowerCase().trim();
      if (!code) return;
      if (activeLanguages.includes(code)) return;
      
      setActiveLanguages([...activeLanguages, code]);
      setNewLangCode('');
  };

  const removeLanguage = (lang: string) => {
      if (activeLanguages.length <= 1) {
          alert("At least one language is required.");
          return;
      }
      setActiveLanguages(activeLanguages.filter(l => l !== lang));
  };

  const handleAiGenerate = async () => {
      if (!aiTopic) return;
      if (isQuestionLocked) return; 

      setIsAiGenerating(true);
      try {
          const schema = await generateSurveySchema(aiTopic, "Generate a complete survey configuration including settings and questions.");
          
          if (schema.title) setTitle(schema.title);
          if (schema.description) setDescription(schema.description);
          
          let aiIsKiosk = isKiosk;

          // Apply Settings
          if (schema.settings) {
              // Only overwrite if explicitly set by AI
              if (typeof schema.settings.is_kiosk === 'boolean') {
                  aiIsKiosk = schema.settings.is_kiosk;
                  setIsKiosk(aiIsKiosk);
              }
              setMaxRes(schema.settings.max_responses ?? null);
              
              // Validate Reward Type (Must match enum)
              const rt = schema.settings.reward_type;
              if (['DIGITAL', 'PHYSICAL', 'NONE'].includes(rt)) {
                  setRewardType(rt as RewardType);
                  setSelectedRewardId(''); // Reset selection as AI cannot select specific ID
              }
              
              const st = schema.settings.status;
              if (['DRAFT', 'ACTIVE', 'PAUSED', 'COMPLETED'].includes(st)) {
                  setStatus(st as SurveyStatus);
              }
          }
          
          // Detect languages from AI response
          const aiLangs = new Set<string>();
          if (schema.title) Object.keys(schema.title).forEach(k => aiLangs.add(k));
          if (schema.description) Object.keys(schema.description).forEach(k => aiLangs.add(k));
          
          if (aiLangs.size === 0) aiLangs.add('en');
          setActiveLanguages(Array.from(aiLangs));

          // Filter out invalid Kiosk questions (Safety Net)
          const validAiQuestions = (schema.questions || []).filter((q: any) => {
              if (aiIsKiosk) {
                  // In Kiosk mode, absolutely forbid input-heavy types
                  const forbidden = ['TEXT', 'DATE', 'MATRIX', 'GEO_LOCATION', 'IMAGE_UPLOAD'];
                  // Double check casing
                  if (forbidden.includes(q.question_type?.toUpperCase())) {
                      console.warn(`[AI-Gen] Removing ${q.question_type} question for Kiosk mode compliance.`);
                      return false;
                  }
              }
              return true;
          });

          // Enforce Max Question Limit ONLY for AI
          const limitedQuestions = validAiQuestions.slice(0, AI_GENERATION_LIMIT);

          const newQuestions = limitedQuestions.map((q: any, idx: number) => {
             const type = q.question_type || (aiIsKiosk ? 'RATING' : 'TEXT');
             // Default required=true unless the AI explicitly set required:false.
             let config = { required: true, ...(q.config || {}) };
             
             // Sanitize AI Results: Ensure Options for Choice Types
             if (['MULTIPLE_CHOICE', 'CHECKBOX', 'RANKING'].includes(type)) {
                 if (!config.options || !Array.isArray(config.options) || config.options.length === 0) {
                     // Fallback options if AI hallucinated the type but forgot options
                     config.options = [
                         { id: `opt-ai-${idx}-1`, label: { en: 'Option 1' } },
                         { id: `opt-ai-${idx}-2`, label: { en: 'Option 2' } }
                     ];
                 } else {
                     // Ensure IDs exist on options
                     config.options = config.options.map((o: any, oIdx: number) => ({
                         ...o,
                         id: o.id || `opt-ai-${idx}-${oIdx}`
                     }));
                 }
             }
             
             // Sanitize Matrix
             if (type === 'MATRIX') {
                 if (!config.rows || config.rows.length === 0) {
                     config.rows = [{ id: `r-${idx}-1`, label: { en: 'Row 1' } }];
                 }
                 if (!config.columns || config.columns.length === 0) {
                     config.columns = [{ id: `c-${idx}-1`, label: { en: 'Column 1' } }];
                 }
             }

             // Sanitize Rating — also repair a hallucinated inverted/broken scale, which
             // would otherwise render zero buttons in the runner.
             if (type === 'RATING') {
                 config.min = Number.isFinite(config.min) ? config.min : 1;
                 config.max = Number.isFinite(config.max) ? config.max : 5;
                 config.step = config.step ?? 1;
                 if (config.min >= config.max) { config.min = 1; config.max = 5; }
             }

             // Carry the AI-suggested vault link only if it's a known attribute id.
             const linkedAttr = attrDefs.some(d => d.id === q.attribute_id) ? q.attribute_id : null;

             return {
                 id: `temp-ai-${Date.now()}-${idx}`,
                 order_index: idx + 1,
                 question_type: type,
                 question_text: q.question_text || { en: 'Question' },
                 config: config,
                 gemini_validation_prompt: q.gemini_validation_prompt || '',
                 attribute_id: linkedAttr,
             };
          });
          
          setQuestions(newQuestions);
          setAiTopic(''); 
      } catch (e) {
          console.error(e);
          alert("AI Generation failed. Please try again.");
      } finally {
          setIsAiGenerating(false);
      }
  };

  const addQuestion = () => {
    if (isQuestionLocked) return;
    
    // Manual creation has NO limit.
    setQuestions(prev => [
      ...prev,
      {
        id: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        order_index: prev.length + 1,
        question_type: isKiosk ? 'RATING' : 'TEXT', // Default changes based on mode
        question_text: {},
        config: { required: true },
        gemini_validation_prompt: ''
      }
    ]);
  };

  const updateQuestion = (id: string, field: string, value: any) => {
    if (isQuestionLocked) return;
    setQuestions(questions.map(q => {
      if (q.id !== id) return q;
      
      // Handle Type Switching - rebuild a clean config so stale keys (e.g. options
      // left over from a CHOICE type, or rows/columns from a MATRIX) never linger and
      // confuse the runner. Only type-agnostic settings and reusable data are carried.
      if (field === 'question_type') {
          const newType = value;
          const newConfig: QuestionConfig = {};
          if (q.config.required !== undefined) newConfig.required = q.config.required;

          // Choice types share an options list, so preserve it across MULTIPLE_CHOICE / CHECKBOX / RANKING.
          if (['MULTIPLE_CHOICE', 'CHECKBOX', 'RANKING'].includes(newType)) {
              newConfig.options = (q.config.options && q.config.options.length > 0)
                  ? q.config.options
                  : [
                      { id: `opt-${Date.now()}-1`, label: { en: 'Option 1' } },
                      { id: `opt-${Date.now()}-2`, label: { en: 'Option 2' } }
                  ];
          }
          // Defaults for Rating
          if (newType === 'RATING') {
              newConfig.min = 1;
              newConfig.max = 5;
              newConfig.step = 1;
          }
          // Defaults for Matrix
          if (newType === 'MATRIX') {
              newConfig.rows = (q.config.rows && q.config.rows.length > 0)
                  ? q.config.rows
                  : [{ id: `row-${Date.now()}-1`, label: { en: 'Row 1' } }];
              newConfig.columns = (q.config.columns && q.config.columns.length > 0)
                  ? q.config.columns
                  : [{ id: `col-${Date.now()}-1`, label: { en: 'Col 1' } }, { id: `col-${Date.now()}-2`, label: { en: 'Col 2' } }];
          }

          return { ...q, question_type: newType, config: newConfig };
      }

      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        // Deep merge support for localized text
        if (parent === 'question_text') {
             return { ...q, question_text: { ...q.question_text, [child]: value } };
        }
        return { ...q, [parent]: { ...(q as any)[parent], [child]: value } };
      }
      return { ...q, [field]: value };
    }));
  };

  const updateQuestionConfig = (id: string, newConfig: Partial<QuestionConfig>) => {
    if (isQuestionLocked) return;
    setQuestions(questions.map(q => {
      if (q.id !== id) return q;
      return { ...q, config: { ...q.config, ...newConfig } };
    }));
  };

  const removeQuestion = (id: string) => {
    if (isQuestionLocked) return;
    setQuestions(questions.filter(q => q.id !== id));
  };

  // --- SMART UI HELPERS FOR OPTIONS ---
  const handleAddOption = (questionId: string) => {
    if (isQuestionLocked) return;
    setQuestions(questions.map(q => {
      if (q.id !== questionId) return q;
      const currentOptions = q.config.options || [];
      const newOption: SurveyOption = {
        id: `opt-${Date.now()}`,
        label: {}
      };
      return {
        ...q,
        config: { ...q.config, options: [...currentOptions, newOption] }
      };
    }));
  };

  const handleUpdateOption = (questionId: string, optionId: string, field: string, value: any) => {
    if (isQuestionLocked) return;
    setQuestions(questions.map(q => {
      if (q.id !== questionId) return q;
      const options = q.config.options?.map(opt => {
        if (opt.id !== optionId) return opt;
        if (field === 'next_question_index') {
          return { ...opt, next_question_index: value === '' ? null : parseInt(value) };
        }
        return { ...opt, label: { ...opt.label, [field]: value } };
      }) || [];
      return { ...q, config: { ...q.config, options } };
    }));
  };

  const handleRemoveOption = (questionId: string, optionId: string) => {
    if (isQuestionLocked) return;
    setQuestions(questions.map(q => {
      if (q.id !== questionId) return q;
      const options = q.config.options?.filter(opt => opt.id !== optionId) || [];
      return { ...q, config: { ...q.config, options } };
    }));
  };

  // --- SMART UI HELPERS FOR MATRIX (ROWS/COLS) ---
  const handleAddMatrixItem = (questionId: string, type: 'rows' | 'columns') => {
      if (isQuestionLocked) return;
      setQuestions(questions.map(q => {
          if (q.id !== questionId) return q;
          const currentItems = (type === 'rows' ? q.config.rows : q.config.columns) || [];
          const newItem: SurveyOption = { id: `${type === 'rows' ? 'r' : 'c'}-${Date.now()}`, label: {} };
          return { ...q, config: { ...q.config, [type]: [...currentItems, newItem] } };
      }));
  };

  const handleUpdateMatrixItem = (questionId: string, type: 'rows' | 'columns', itemId: string, lang: string, value: string) => {
      if (isQuestionLocked) return;
      setQuestions(questions.map(q => {
          if (q.id !== questionId) return q;
          const items = (type === 'rows' ? q.config.rows : q.config.columns)?.map(item => {
              if (item.id !== itemId) return item;
              return { ...item, label: { ...item.label, [lang]: value } };
          }) || [];
          return { ...q, config: { ...q.config, [type]: items } };
      }));
  };

  const handleRemoveMatrixItem = (questionId: string, type: 'rows' | 'columns', itemId: string) => {
      if (isQuestionLocked) return;
      setQuestions(questions.map(q => {
          if (q.id !== questionId) return q;
          const items = (type === 'rows' ? q.config.rows : q.config.columns)?.filter(i => i.id !== itemId) || [];
          return { ...q, config: { ...q.config, [type]: items } };
      }));
  };

  const handleSave = async () => {
    // Validate title exists in at least one active language
    const hasTitle = activeLanguages.some(lang => title[lang] && title[lang]!.trim() !== '');
    if (!hasTitle) return alert(`Title is required in at least one active language (${activeLanguages.join(', ')}).`);

    if (rewardType !== 'NONE' && !selectedRewardId) return alert('Please select a specific reward config.');

    // --- Validate questions so we never persist a broken / unanswerable survey ---
    const labelFilled = (lbl: any) =>
      !!lbl && Object.values(lbl).some((v: any) => typeof v === 'string' && v.trim() !== '');

    if (questions.length === 0) {
      return alert('Add at least one question before saving.');
    }
    for (let i = 0; i < questions.length; i++) {
      const q = questions[i];
      const n = i + 1;
      if (!labelFilled(q.question_text)) {
        return alert(`Question ${n}: add the question text in at least one language.`);
      }
      if (['MULTIPLE_CHOICE', 'CHECKBOX', 'RANKING'].includes(q.question_type)) {
        const filled = (q.config.options || []).filter(o => labelFilled(o.label));
        if (filled.length < 2) {
          return alert(`Question ${n}: choice questions need at least 2 options with labels.`);
        }
      }
      if (q.question_type === 'MATRIX') {
        const rows = (q.config.rows || []).filter(r => labelFilled(r.label));
        const cols = (q.config.columns || []).filter(c => labelFilled(c.label));
        if (rows.length < 1 || cols.length < 1) {
          return alert(`Question ${n}: matrix questions need at least one row and one column, each with a label.`);
        }
      }
      // A broken rating scale (min >= max, or NaN from a cleared field) renders ZERO
      // buttons in the runner — a required question would trap the respondent forever.
      if (q.question_type === 'RATING') {
        const min = q.config.min ?? 1;
        const max = q.config.max ?? 5;
        if (!Number.isFinite(min) || !Number.isFinite(max) || min >= max) {
          return alert(`Question ${n}: rating scale needs numeric Min < Max (e.g. 1 to 5).`);
        }
      }
      // Validate option-level skip logic: the target must be a real question number and
      // not the question itself (a self-jump would loop forever in the runner).
      if (q.question_type === 'MULTIPLE_CHOICE') {
        for (const opt of q.config.options || []) {
          const t = opt.next_question_index;
          if (t !== null && t !== undefined) {
            if (!Number.isInteger(t) || t < 1 || t > questions.length) {
              return alert(`Question ${n}: an option jumps to Q${t}, but this survey only has questions 1–${questions.length}.`);
            }
            if (t === n) {
              return alert(`Question ${n}: an option jumps back to itself — remove that skip rule.`);
            }
          }
        }
      }
    }

    // Kiosk surveys are touch-only: typing/desktop-input types must never be saved on
    // one. The type dropdown hides these in kiosk mode, but questions created BEFORE
    // the kiosk toggle was switched on keep their old type — catch them here.
    if (isKiosk) {
      const KIOSK_FORBIDDEN = ['TEXT', 'DATE', 'MATRIX', 'GEO_LOCATION', 'IMAGE_UPLOAD'];
      const bad = questions
        .map((q, i) => ({ type: q.question_type, n: i + 1 }))
        .filter(({ type }) => KIOSK_FORBIDDEN.includes(type));
      if (bad.length > 0) {
        return alert(
          `Kiosk mode is touch-only. ${bad.map(b => `Q${b.n} (${b.type})`).join(', ')} ` +
          `can't be used on a kiosk — change them to Rating / Multiple Choice / Checkbox / Ranking, or turn Kiosk mode off.`
        );
      }
    }

    if (maxRes !== null && maxRes !== undefined && maxRes < 1) {
      return alert('Max responses must be at least 1 (leave it empty for unlimited).');
    }

    // A brand-new kiosk survey must have an access code so the URL alone cannot grant
    // access (an unprotected public kiosk would feel "hacked" to the owner).
    if (isKiosk && !editingId && !kioskCode.trim()) {
      return alert('Kiosk surveys require an access code. Set one before saving.');
    }

    if (memberBox && (rewardType !== 'PHYSICAL' || !selectedRewardId)) {
      return alert('Member Mystery Box needs a Loot Box reward — set the Reward to a Loot Box (those are the prizes members can win).');
    }

    setIsSaving(true);
    
    try {
      const surveyPayload = {
        title: title,
        description: description,
        status: status,
        reward_type: rewardType,
        digital_reward_id: rewardType === 'DIGITAL' ? selectedRewardId : null,
        loot_table_id: rewardType === 'PHYSICAL' ? selectedRewardId : null,
        max_responses: maxRes,
        is_kiosk: isKiosk,
        is_live: isLive,
        completion_box: memberBox,
        require_login: requireLogin,
        requested_attribute_ids: requestedAttributeIds.length ? requestedAttributeIds : null,
        data_reward_points: requestedAttributeIds.length ? dataRewardPoints : null,
        completion_reward_points: Number.isFinite(completionReward) ? Math.max(0, completionReward) : 1,
        target_attributes: (() => {
          const t = Object.entries(targetValues)
            .filter(([id, v]) => v && requestedAttributeIds.includes(id))
            .map(([attribute_id, value]) => ({ attribute_id, value }));
          return t.length ? t : null;
        })(),
      };

      let surveyId = editingId;
      if (editingId) {
        await updateSurvey(editingId, surveyPayload, questions);
      } else {
        const created = await createSurvey(surveyPayload, questions);
        surveyId = created?.id;
      }
      // Set the kiosk access code (creator-only, server-enforced). Only write when a
      // code was typed — an empty field on edit keeps whatever code is already stored.
      if (surveyId && isKiosk && kioskCode.trim()) {
        try { await setKioskCode(surveyId, kioskCode.trim()); }
        catch (e) { console.error('Failed to set kiosk code', e); }
      }
      navigate('/research/campaigns');
    } catch (e) {
      console.error(e);
      alert(`Failed to ${editingId ? 'update' : 'save'} survey.`);
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
      return <div className="flex justify-center items-center h-64"><Loader2 className="animate-spin text-indigo-500" size={32} /></div>;
  }

  return (
    <div className="space-y-8 animate-in slide-in-from-right duration-300 pb-12">
      <div className="flex items-center gap-4 border-b border-slate-700 pb-4">
         <button onClick={() => navigate('/research/campaigns')} className="text-slate-400 hover:text-white transition-colors text-sm">
           &larr; Back to List
         </button>
         <h2 className="text-xl font-bold text-white">
           {editingId ? 'Edit Survey' : 'Create New Survey'}
         </h2>
      </div>
      
      {/* AI Generator Box */}
      {!editingId && !isQuestionLocked && (
          <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-xl p-6 border border-indigo-500/30 flex flex-col gap-4">
             <div>
                 <h3 className="text-lg font-bold text-white flex items-center gap-2"><Brain className="text-indigo-400"/> AI Draft</h3>
                 <p className="text-slate-300 text-sm mt-1">Describe your goal (e.g. "Survey about coffee farming in Mondulkiri") and let AI build the questions.</p>
             </div>
             <div className="flex flex-col gap-3">
                 <textarea 
                    value={aiTopic}
                    onChange={e => setAiTopic(e.target.value)}
                    placeholder="Describe your survey topic in detail..." 
                    className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white text-sm focus:ring-2 focus:ring-indigo-500 outline-none resize-none h-24 shadow-inner"
                    onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), handleAiGenerate())}
                 />
                 <div className="flex justify-end">
                    <button 
                        onClick={handleAiGenerate}
                        disabled={isAiGenerating || !aiTopic}
                        className="bg-indigo-600 hover:bg-indigo-500 text-white px-6 py-2.5 rounded-lg font-bold flex items-center gap-2 disabled:opacity-50 transition-all shadow-lg shadow-indigo-500/20 active:scale-95"
                    >
                        {isAiGenerating ? <Loader2 className="animate-spin" size={18}/> : <Sparkles size={18}/>} Generate Draft
                    </button>
                 </div>
             </div>
          </div>
      )}

      {/* Lock Warning */}
      {isQuestionLocked && (
          <div className="bg-orange-900/20 border border-orange-500/30 p-4 rounded-xl flex items-start gap-3">
              <AlertTriangle className="text-orange-400 shrink-0 mt-0.5" size={20} />
              <div>
                  <h4 className="text-orange-300 font-bold text-sm">Modification Locked</h4>
                  <p className="text-orange-200/80 text-xs mt-1">
                      This survey has received <strong>{existingResponseCount} response(s)</strong>. 
                      To maintain data integrity, questions and options cannot be edited or deleted. 
                      You can still update the title, description, and status.
                  </p>
              </div>
          </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column: Settings */}
        <div className="space-y-6 lg:col-span-1">
          <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 space-y-4">
             
             {/* MODE TOGGLES */}
             <div className="space-y-3">
                 <label className="block text-xs uppercase text-slate-500 font-semibold">Access Modes</label>
                 {/* Kiosk Toggle */}
                 <div className="bg-slate-900 p-4 rounded-lg border border-slate-700 flex items-center justify-between">
                     <div className="flex flex-col">
                         <span className="text-white font-bold text-sm flex items-center gap-2"><MonitorPlay size={16} className="text-purple-400" /> Kiosk Mode</span>
                         <span className="text-xs text-slate-500">Touch-only interface</span>
                     </div>
                     <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={isKiosk}
                          onChange={e => {
                            const on = e.target.checked;
                            setIsKiosk(on);
                            // Kiosk is anonymous/shop-floor: members-only login makes no sense there.
                            if (on) setRequireLogin(false);
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                     </label>
                 </div>

                 {/* Live Mode Toggle — present on a big screen, audience votes by QR */}
                 <div className="bg-slate-900 p-4 rounded-lg border border-slate-700 flex items-center justify-between">
                     <div className="flex flex-col">
                         <span className="text-white font-bold text-sm flex items-center gap-2"><Radio size={16} className="text-indigo-400" /> Live Mode</span>
                         <span className="text-xs text-slate-500">Big-screen results, audience votes by QR (best with choice/rating questions)</span>
                     </div>
                     <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={isLive} onChange={e => setIsLive(e.target.checked)} className="sr-only peer" />
                        <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                     </label>
                 </div>
                 {isLive && editingId && (
                     <button type="button" onClick={() => navigate(`/research/campaigns/${editingId}/present`)} className="w-full bg-indigo-600/20 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-600/30 rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2"><Radio size={15} /> Open live presenter</button>
                 )}

                 {/* Member Mystery Box — members open a box for an owner-set reward on completion */}
                 <div className="bg-slate-900 p-4 rounded-lg border border-slate-700 flex items-center justify-between">
                     <div className="flex flex-col pr-3">
                         <span className="text-white font-bold text-sm flex items-center gap-2"><Gift size={16} className="text-yellow-400" /> Member Mystery Box</span>
                         <span className="text-xs text-slate-500">Signed-in members open a mystery box on completion. Set the prizes via a <strong>Loot Box reward</strong> below.</span>
                     </div>
                     <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={memberBox} onChange={e => {
                          const on = e.target.checked;
                          setMemberBox(on);
                          if (on) { setRequireLogin(true); setRewardType('PHYSICAL'); }  // members only; box = loot table
                        }} className="sr-only peer" />
                        <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                     </label>
                 </div>

                 {/* Kiosk Access Code — a kiosk URL alone must not grant access. */}
                 {isKiosk && (
                     <div className="bg-slate-900 p-4 rounded-lg border border-slate-700 space-y-2">
                         <span className="text-white font-bold text-sm flex items-center gap-2"><Lock size={16} className="text-purple-400" /> Kiosk Access Code</span>
                         <input
                            type="text"
                            value={kioskCode}
                            onChange={e => setKioskCodeValue(e.target.value)}
                            placeholder={editingId ? 'Leave blank to keep current code' : 'Set a device unlock code'}
                            className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm tracking-widest outline-none focus:border-purple-500"
                         />
                         <p className="text-[10px] text-slate-500">
                             The device unlocks with this code (or a <code className="text-slate-400">?code=</code> link). Anyone with just the URL stays locked out.
                             {editingId && ' The current code is hidden; type a new one to replace it.'}
                         </p>
                     </div>
                 )}

                 {/* Member Login Toggle */}
                 <div className={`bg-slate-900 p-4 rounded-lg border border-slate-700 flex items-center justify-between ${isKiosk ? 'opacity-50' : ''}`}>
                     <div className="flex flex-col">
                         <span className="text-white font-bold text-sm flex items-center gap-2"><Lock size={16} className={requireLogin ? "text-orange-400" : "text-slate-400"} /> Member Only</span>
                         <span className="text-xs text-slate-500">{isKiosk ? 'Not available in Kiosk mode' : requireLogin ? 'Log In Required' : 'Public Access (Guests)'}</span>
                     </div>
                     <label className={`relative inline-flex items-center ${isKiosk ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                        <input
                            type="checkbox"
                            checked={requireLogin}
                            onChange={e => setRequireLogin(e.target.checked)}
                            disabled={isKiosk}
                            className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                     </label>
                 </div>
                 {rewardType === 'DIGITAL' && !requireLogin && !isKiosk && <p className="text-[10px] text-orange-400 italic mt-1">* Guests cannot receive digital rewards.</p>}
             </div>

             <div className="flex justify-between items-center border-t border-slate-700 pt-4">
                 <h3 className="font-bold text-white flex items-center gap-2"><Globe size={18} className="text-indigo-400"/> Localizations</h3>
             </div>
             
             {/* Language Manager */}
             <div className="bg-slate-900 p-3 rounded-lg border border-slate-700">
                 <div className="flex flex-wrap gap-2 mb-3">
                     {activeLanguages.map(lang => (
                         <span key={lang} className="text-xs font-mono uppercase bg-slate-700 text-slate-300 px-2 py-1 rounded flex items-center gap-1 border border-slate-600">
                             {lang}
                             <button onClick={() => removeLanguage(lang)} className="hover:text-red-400"><X size={10} /></button>
                         </span>
                     ))}
                 </div>
                 <div className="flex gap-2">
                     <input 
                        value={newLangCode}
                        onChange={e => setNewLangCode(e.target.value.substring(0,2))}
                        placeholder="Add lang (e.g. kh)"
                        className="w-20 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white uppercase outline-none"
                     />
                     <button onClick={addLanguage} className="text-xs bg-indigo-600 text-white px-3 rounded hover:bg-indigo-500 font-medium">Add</button>
                 </div>
             </div>

             <div className="border-t border-slate-700 pt-4 space-y-4">
                {activeLanguages.map(lang => (
                    <div key={`title-${lang}`}>
                        <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Title ({lang.toUpperCase()})</label>
                        <input 
                            value={title[lang] || ''} 
                            onChange={e => setTitle(prev => ({ ...prev, [lang]: e.target.value }))} 
                            className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm" 
                            placeholder={`Title in ${lang}...`} 
                        />
                    </div>
                ))}
             </div>

             <div className="border-t border-slate-700 pt-4 space-y-4">
                {activeLanguages.map(lang => (
                    <div key={`desc-${lang}`}>
                        <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Description ({lang.toUpperCase()})</label>
                        <textarea 
                            value={description[lang] || ''} 
                            onChange={e => setDescription(prev => ({ ...prev, [lang]: e.target.value }))} 
                            className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm h-16 resize-none" 
                            placeholder={`Description in ${lang}...`} 
                        />
                    </div>
                ))}
             </div>
             
             <div className="border-t border-slate-700 pt-4">
               <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Status</label>
               <select value={status} onChange={e => setStatus(e.target.value as SurveyStatus)} className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm">
                 <option value="DRAFT">Draft</option>
                 <option value="ACTIVE">Active</option>
                 <option value="PAUSED">Paused</option>
                 <option value="COMPLETED">Completed</option>
               </select>
             </div>
             
             {/* Max Respondents */}
             <div>
               <label className="block text-xs uppercase text-slate-500 font-semibold mb-2">Max Respondents</label>
               <div className="bg-slate-900 p-3 rounded-lg border border-slate-600 flex items-center gap-4">
                   <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer select-none">
                       <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${maxRes === null ? 'bg-indigo-600 border-indigo-500' : 'border-slate-500 bg-slate-800'}`}>
                           {maxRes === null && <Check size={14} className="text-white"/>}
                       </div>
                       <input 
                           type="checkbox" 
                           checked={maxRes === null} 
                           onChange={(e) => setMaxRes(e.target.checked ? null : 100)} 
                           className="hidden"
                        />
                       <span className="flex items-center gap-1"><Infinity size={14} /> Unlimited</span>
                   </label>
                   
                   {maxRes !== null && (
                       <input
                           type="number"
                           value={maxRes}
                           onChange={e => {
                               // A cleared field parses to NaN, which would silently save as
                               // NULL (unlimited); pin it to 0 so validation catches it instead.
                               const v = parseInt(e.target.value);
                               setMaxRes(Number.isNaN(v) ? 0 : v);
                           }}
                           className="flex-1 bg-slate-800 border border-slate-500 rounded p-1 text-white text-sm text-center focus:border-indigo-500 outline-none"
                           placeholder="Limit"
                       />
                   )}
               </div>
             </div>

             {/* Answer Reward (creator-tunable points for completing — compete for respondents) */}
             <div>
               <label className="block text-xs uppercase text-slate-500 font-semibold mb-2">Answer Reward (points)</label>
               <input
                 type="number"
                 min={0}
                 step="1"
                 value={completionReward}
                 onChange={e => setCompletionReward(e.target.value === '' ? 0 : parseInt(e.target.value))}
                 className="w-full bg-slate-900 border border-slate-600 rounded-lg p-2.5 text-white text-sm outline-none focus:border-indigo-500"
               />
               <p className="text-[10px] text-slate-500 mt-1">Points each respondent earns for completing. Default 1 — raise it to compete for respondents.</p>
             </div>

             {/* Reward Selector */}
             <div>
               <label className="block text-xs uppercase text-slate-500 font-semibold mb-1">Reward Type</label>
               <div className="flex bg-slate-900 p-1 rounded-lg border border-slate-700 mb-2">
                   <button onClick={() => setRewardType('DIGITAL')} className={`flex-1 text-xs py-1.5 rounded ${rewardType === 'DIGITAL' ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}>Digital</button>
                   <button onClick={() => setRewardType('PHYSICAL')} className={`flex-1 text-xs py-1.5 rounded ${rewardType === 'PHYSICAL' ? 'bg-indigo-600 text-white' : 'text-slate-400'}`}>Physical</button>
                   <button onClick={() => { setRewardType('NONE'); setSelectedRewardId(''); }} className={`flex-1 text-xs py-1.5 rounded ${rewardType === 'NONE' ? 'bg-slate-700 text-white' : 'text-slate-400'}`}>None</button>
               </div>

               {rewardType === 'DIGITAL' && (
                   <select 
                     value={selectedRewardId} 
                     onChange={e => setSelectedRewardId(e.target.value)} 
                     className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm"
                   >
                     <option value="">-- Select Digital Reward --</option>
                     {availDigiRewards.map(r => (
                       <option key={r.id} value={r.id}>{r.name} ({r.amount} {r.currency})</option>
                     ))}
                   </select>
               )}

               {rewardType === 'PHYSICAL' && (
                   <select
                     value={selectedRewardId}
                     onChange={e => setSelectedRewardId(e.target.value)}
                     className="w-full bg-slate-900 border border-slate-600 rounded p-2 text-white text-sm"
                   >
                     <option value="">-- Select Loot Table --</option>
                     {availLootTables.map(t => (
                       <option key={t.id} value={t.id}>{t.name}</option>
                     ))}
                   </select>
               )}
             </div>

             {/* DataFi: Request Private Data */}
             <div className="border-t border-slate-700 pt-4">
               <label className="text-xs uppercase text-slate-500 font-semibold flex items-center gap-1.5 mb-1">
                 <ShieldCheck size={13} className="text-emerald-400" /> Request Private Data
               </label>
               <p className="text-[11px] text-slate-500 mb-3">
                 Ask respondents to share fields from their encrypted vault. Sharing is always
                 optional for them, and they're paid the data reward you set below.
               </p>
               <div className="grid grid-cols-2 gap-1.5">
                 {attrDefs.map(def => {
                   const active = requestedAttributeIds.includes(def.id);
                   return (
                     <button
                       key={def.id}
                       type="button"
                       onClick={() =>
                         setRequestedAttributeIds(prev =>
                           active ? prev.filter(k => k !== def.id) : [...prev, def.id],
                         )
                       }
                       className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                         active
                           ? 'bg-emerald-900/30 border-emerald-500/50 text-white'
                           : 'bg-slate-900/40 border-slate-700 text-slate-400 hover:bg-slate-800'
                       }`}
                     >
                       <span
                         className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                           active ? 'bg-emerald-500 border-emerald-500' : 'border-slate-600'
                         }`}
                       >
                         {active && <Check size={10} className="text-white" />}
                       </span>
                       {def.label_en}
                     </button>
                   );
                 })}
                 {attrDefs.length === 0 && (
                   <p className="col-span-2 text-[11px] text-slate-500 italic">No attribute catalog (run datafi.sql).</p>
                 )}
               </div>

               {requestedAttributeIds.length > 0 && (
                 <div className="mt-3 bg-slate-900 p-3 rounded-lg border border-slate-700">
                   <label className="block text-[10px] uppercase text-slate-500 font-bold mb-2">
                     Data Reward boost — points (sharing already earns +3 by default)
                   </label>
                   <input
                     type="number"
                     min={0}
                     step="1"
                     value={dataRewardPoints ?? ''}
                     onChange={e => setDataRewardPoints(e.target.value === '' ? null : parseInt(e.target.value))}
                     placeholder="Leave blank for the +3 baseline"
                     className="w-full bg-slate-800 border border-slate-600 rounded p-2 text-white text-sm outline-none focus:border-emerald-500"
                   />
                   <p className="text-[10px] text-slate-500 mt-2">
                     Requesting {requestedAttributeIds.length} attribute(s). Respondents unlock their vault
                     and consent per submission.
                   </p>

                   {/* Audience targeting (optional) — matches over value_summary without decrypting */}
                   {(() => {
                     const targetable = requestedAttributeIds
                       .map(id => attrDefs.find(d => d.id === id))
                       .filter((d): d is AttributeDefinition => !!d && (d.data_type === 'SELECTION' || d.data_type === 'BOOLEAN'));
                     if (targetable.length === 0) return null;
                     return (
                       <div className="mt-3 pt-3 border-t border-slate-700/60">
                         <label className="block text-[10px] uppercase text-slate-500 font-bold mb-2">
                           Audience targeting (optional)
                         </label>
                         <div className="space-y-2">
                           {targetable.map(def => {
                             const opts = def.data_type === 'BOOLEAN' ? ['yes', 'no'] : (ATTRIBUTE_OPTIONS[def.id] || []);
                             return (
                               <div key={def.id} className="flex items-center gap-2">
                                 <span className="text-xs text-slate-400 flex-1 truncate">{def.label_en}</span>
                                 <select
                                   value={targetValues[def.id] || ''}
                                   onChange={e => setTargetValues(prev => ({ ...prev, [def.id]: e.target.value }))}
                                   className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-white outline-none focus:border-emerald-500"
                                 >
                                   <option value="">Any</option>
                                   {opts.map(o => <option key={o} value={o}>{o}</option>)}
                                 </select>
                               </div>
                             );
                           })}
                         </div>
                         <div className="mt-2 text-[11px] text-emerald-300">
                           {reachLoading ? 'Estimating reach…'
                             : reach === null ? 'Set a target value to estimate reach.'
                             : reach === 0 ? '≈ Fewer than 5 people match (hidden for privacy).'
                             : `≈ ${reach} people match this audience.`}
                         </div>
                         <p className="text-[10px] text-slate-500 mt-1">
                           Only respondents matching every target value earn the reward (checked privately on their device).
                         </p>
                       </div>
                     );
                   })()}
                 </div>
               )}
             </div>
          </div>
        </div>

        {/* Right Column: Smart Question Builder */}
        <div className="lg:col-span-2 space-y-6">
           <div className={`bg-slate-800 p-6 rounded-xl border border-slate-700 min-h-[600px] flex flex-col ${isQuestionLocked ? 'opacity-90 grayscale-[0.3]' : ''}`}>
              <div className="flex justify-between items-center mb-6">
                 <h3 className="font-bold text-white flex items-center gap-2"><List size={18} className="text-blue-400"/> Questions ({questions.length})</h3>
                 {!isQuestionLocked && (
                     <button 
                        onClick={addQuestion} 
                        className="text-xs bg-blue-600/20 text-blue-400 hover:bg-blue-600/30 px-3 py-1.5 rounded-full font-medium transition-colors border border-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                       + Add Question
                     </button>
                 )}
              </div>

              <div className="space-y-6 flex-1">
                 {questions.map((q, idx) => (
                   <div key={q.id} className="bg-slate-900/50 p-4 rounded-lg border border-slate-700 relative group animate-in slide-in-from-bottom-2">
                      {isQuestionLocked && (
                          <div className="absolute inset-0 z-10 bg-transparent cursor-not-allowed" title="Editing disabled due to existing responses" />
                      )}
                      
                      <div className="flex justify-between items-start mb-4">
                         <div className="flex items-center gap-3">
                            <span className="bg-slate-800 text-slate-400 text-xs font-mono px-2 py-1 rounded border border-slate-700">Q{idx + 1}</span>
                            <select 
                              value={q.question_type}
                              onChange={e => updateQuestion(q.id, 'question_type', e.target.value)}
                              disabled={isQuestionLocked}
                              className="bg-slate-800 border border-slate-600 rounded text-xs text-white p-1.5 outline-none focus:border-indigo-500 disabled:opacity-50"
                            >
                              <option value="RATING">Rating (Scale)</option>
                              <option value="MULTIPLE_CHOICE">Multiple Choice</option>
                              <option value="CHECKBOX">Checkbox</option>
                              <option value="RANKING">Ranking (Drag & Drop)</option>
                              {/* If kiosk was toggled ON after this question was created with a now-
                                  forbidden type, keep its real type visible (and flagged) instead of
                                  letting the select silently display the wrong one. */}
                              {isKiosk && ['TEXT', 'DATE', 'MATRIX', 'GEO_LOCATION', 'IMAGE_UPLOAD'].includes(q.question_type) && (
                                  <option value={q.question_type}>⚠ {q.question_type} (not allowed in Kiosk)</option>
                              )}
                              {!isKiosk && <option value="TEXT">Text Input</option>}
                              {!isKiosk && <option value="DATE">Date Picker</option>}
                              {!isKiosk && <option value="MATRIX">Matrix / Likert</option>}
                              {!isKiosk && <option value="GEO_LOCATION">Geo-Location (Map)</option>}
                              {!isKiosk && <option value="IMAGE_UPLOAD">Image Upload (Proof)</option>}
                            </select>
                         </div>
                         {!isQuestionLocked && (
                             <button onClick={() => removeQuestion(q.id)} className="text-slate-600 hover:text-red-400 transition-colors"><Trash2 size={16}/></button>
                         )}
                      </div>

                      <div className="space-y-3 mb-4">
                          {activeLanguages.map(lang => (
                              <div key={lang} className="flex gap-2 items-center">
                                 <div className="w-8 flex justify-center"><span className="text-xs font-bold text-slate-500 uppercase">{lang}</span></div>
                                 <input 
                                   placeholder={`Question text in ${lang}...`} 
                                   value={q.question_text[lang] || ''}
                                   onChange={e => updateQuestion(q.id, `question_text.${lang}`, e.target.value)}
                                   disabled={isQuestionLocked}
                                   className="flex-1 bg-transparent border-b border-slate-700 focus:border-indigo-500 outline-none text-white text-sm py-1 placeholder-slate-600 transition-colors disabled:opacity-50 disabled:border-transparent"
                                 />
                              </div>
                          ))}
                      </div>

                      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-900/30 p-3 rounded border border-slate-700/50">
                          {q.question_type === 'RATING' && (
                              <>
                                  <div>
                                      <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Min Value</label>
                                      <input 
                                          type="number" 
                                          value={q.config.min ?? 1} 
                                          onChange={(e) => updateQuestionConfig(q.id, { min: parseInt(e.target.value) })}
                                          disabled={isQuestionLocked}
                                          className="w-full bg-slate-800 text-xs border border-slate-600 rounded px-2 py-1 text-white outline-none focus:border-indigo-500"
                                      />
                                  </div>
                                  <div>
                                      <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Max Value</label>
                                      <input 
                                          type="number" 
                                          value={q.config.max ?? 5} 
                                          onChange={(e) => updateQuestionConfig(q.id, { max: parseInt(e.target.value) })}
                                          disabled={isQuestionLocked}
                                          className="w-full bg-slate-800 text-xs border border-slate-600 rounded px-2 py-1 text-white outline-none focus:border-indigo-500"
                                      />
                                  </div>
                                  <div>
                                      <label className="text-[10px] uppercase text-slate-500 font-bold block mb-1">Step</label>
                                      <input 
                                          type="number" 
                                          value={q.config.step ?? 1} 
                                          onChange={(e) => updateQuestionConfig(q.id, { step: parseInt(e.target.value) })}
                                          disabled={isQuestionLocked}
                                          className="w-full bg-slate-800 text-xs border border-slate-600 rounded px-2 py-1 text-white outline-none focus:border-indigo-500"
                                      />
                                  </div>
                              </>
                          )}
                          
                          <div className="col-span-2 md:col-span-2">
                              <label className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1 mb-1">
                                  <ShieldCheck size={10} className="text-emerald-400" /> Link to profile (vault)
                              </label>
                              <select
                                  className="w-full bg-slate-800 text-xs border border-slate-600 rounded px-2 py-1.5 text-white outline-none focus:border-emerald-500 disabled:opacity-50"
                                  value={q.attribute_id || ''}
                                  onChange={(e) => updateQuestion(q.id, 'attribute_id', e.target.value || null)}
                                  disabled={isQuestionLocked}
                              >
                                  <option value="">Not linked</option>
                                  {attrDefs.map(d => <option key={d.id} value={d.id}>{d.label_en}</option>)}
                              </select>
                          </div>

                          <div className="col-span-2 md:col-span-2">
                              <label className="text-[10px] uppercase text-slate-500 font-bold flex items-center gap-1 mb-1">
                                  <Sparkles size={10} /> AI Validation Prompt (Optional)
                              </label>
                              <input
                                  className="w-full bg-slate-800 text-xs border border-slate-600 rounded px-2 py-1 text-white outline-none focus:border-indigo-500 placeholder-slate-600"
                                  placeholder="e.g. Ensure the answer mentions a specific location or detail"
                                  value={q.gemini_validation_prompt || ''}
                                  onChange={(e) => updateQuestion(q.id, 'gemini_validation_prompt', e.target.value)}
                                  disabled={isQuestionLocked}
                              />
                          </div>
                      </div>

                      {/* --- MATRIX UI (NEW) --- */}
                      {q.question_type === 'MATRIX' && (
                          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6 pl-2 border-l-2 border-slate-800">
                              {/* ROWS EDITOR */}
                              <div className="bg-slate-800/30 p-3 rounded">
                                  <div className="flex justify-between items-center mb-2 border-b border-slate-700/50 pb-1">
                                      <span className="text-xs text-indigo-400 font-bold uppercase flex items-center gap-1"><Grid3X3 size={12}/> Rows (Items)</span>
                                      {!isQuestionLocked && <button onClick={() => handleAddMatrixItem(q.id, 'rows')} className="text-indigo-400 hover:text-white p-1"><Plus size={14}/></button>}
                                  </div>
                                  <div className="space-y-2">
                                      {q.config.rows?.map((row, rIdx) => (
                                          <div key={row.id} className="group/item">
                                              <div className="flex items-center gap-2 mb-1">
                                                  <span className="text-[10px] text-slate-600 w-4">{rIdx+1}</span>
                                                  <div className="flex-1">
                                                      {activeLanguages.map(lang => (
                                                          <input 
                                                              key={lang}
                                                              placeholder={`Row in ${lang}`} 
                                                              value={row.label[lang] || ''} 
                                                              onChange={(e) => handleUpdateMatrixItem(q.id, 'rows', row.id, lang, e.target.value)}
                                                              disabled={isQuestionLocked}
                                                              className="bg-transparent text-xs text-slate-300 w-full border-b border-slate-700 focus:border-indigo-500 outline-none mb-1"
                                                          />
                                                      ))}
                                                  </div>
                                                  {!isQuestionLocked && <button onClick={() => handleRemoveMatrixItem(q.id, 'rows', row.id)} className="text-slate-600 hover:text-red-400 opacity-100 lg:opacity-0 lg:group-hover/item:opacity-100"><X size={12}/></button>}
                                              </div>
                                          </div>
                                      ))}
                                  </div>
                              </div>

                              {/* COLUMNS EDITOR */}
                              <div className="bg-slate-800/30 p-3 rounded">
                                  <div className="flex justify-between items-center mb-2 border-b border-slate-700/50 pb-1">
                                      <span className="text-xs text-emerald-400 font-bold uppercase flex items-center gap-1"><Grid3X3 size={12}/> Columns (Options)</span>
                                      {!isQuestionLocked && <button onClick={() => handleAddMatrixItem(q.id, 'columns')} className="text-emerald-400 hover:text-white p-1"><Plus size={14}/></button>}
                                  </div>
                                  <div className="space-y-2">
                                      {q.config.columns?.map((col, cIdx) => (
                                          <div key={col.id} className="group/item">
                                              <div className="flex items-center gap-2 mb-1">
                                                  <span className="text-[10px] text-slate-600 w-4">{cIdx+1}</span>
                                                  <div className="flex-1">
                                                      {activeLanguages.map(lang => (
                                                          <input 
                                                              key={lang}
                                                              placeholder={`Col in ${lang}`} 
                                                              value={col.label[lang] || ''} 
                                                              onChange={(e) => handleUpdateMatrixItem(q.id, 'columns', col.id, lang, e.target.value)}
                                                              disabled={isQuestionLocked}
                                                              className="bg-transparent text-xs text-slate-300 w-full border-b border-slate-700 focus:border-emerald-500 outline-none mb-1"
                                                          />
                                                      ))}
                                                  </div>
                                                  {!isQuestionLocked && <button onClick={() => handleRemoveMatrixItem(q.id, 'columns', col.id)} className="text-slate-600 hover:text-red-400 opacity-100 lg:opacity-0 lg:group-hover/item:opacity-100"><X size={12}/></button>}
                                              </div>
                                          </div>
                                      ))}
                                  </div>
                              </div>
                          </div>
                      )}

                      {(q.question_type === 'MULTIPLE_CHOICE' || q.question_type === 'CHECKBOX' || q.question_type === 'RANKING') && (
                          <div className="space-y-2 pl-8 border-l-2 border-slate-800 mt-4">
                               {q.config.options?.map((opt, optIdx) => (
                                 <div key={opt.id} className="bg-slate-800/50 p-2 rounded">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-xs text-slate-500 font-mono">Option {optIdx + 1}</span>
                                        {!isQuestionLocked && (
                                            <button onClick={() => handleRemoveOption(q.id, opt.id)} className="text-slate-600 hover:text-red-400 p-1"><X size={14}/></button>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        {activeLanguages.map(lang => (
                                            <div key={lang} className="flex gap-2 items-center">
                                                <span className="text-[10px] text-slate-600 uppercase w-6 text-right">{lang}</span>
                                                <input 
                                                    placeholder="Option Label" 
                                                    value={opt.label[lang] || ''} 
                                                    onChange={(e) => handleUpdateOption(q.id, opt.id, lang, e.target.value)}
                                                    disabled={isQuestionLocked}
                                                    className="bg-transparent text-sm text-slate-200 outline-none w-full border-b border-transparent focus:border-indigo-500/50 disabled:opacity-50"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                    
                                    {/* OPTION-LEVEL SKIP LOGIC */}
                                    {q.question_type === 'MULTIPLE_CHOICE' && (
                                        <div className="mt-2 pt-2 border-t border-slate-700/50 flex items-center gap-2">
                                            <span className="text-[10px] text-slate-500 uppercase font-bold flex items-center gap-1">
                                                <ArrowRightCircle size={10} /> Skip Logic
                                            </span>
                                            <input 
                                                type="number" 
                                                placeholder="Jump to Q#"
                                                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-white w-20 outline-none focus:border-indigo-500"
                                                value={opt.next_question_index ?? ''}
                                                onChange={(e) => handleUpdateOption(q.id, opt.id, 'next_question_index', e.target.value)}
                                                disabled={isQuestionLocked}
                                            />
                                            <span className="text-[10px] text-slate-600 italic">Target Question Index</span>
                                        </div>
                                    )}
                                 </div>
                               ))}
                               {!isQuestionLocked && (
                                   <button onClick={() => handleAddOption(q.id)} className="text-xs text-indigo-400 hover:text-indigo-300 flex items-center gap-1 font-medium mt-2">
                                     <Plus size={12} /> Add {q.question_type === 'RANKING' ? 'Item' : 'Option'}
                                   </button>
                               )}
                          </div>
                      )}
                      
                      <div className="flex justify-between mt-4 items-center">
                        <label className={`flex items-center gap-2 text-xs text-slate-400 ${!isQuestionLocked ? 'cursor-pointer' : ''}`}>
                            <input 
                              type="checkbox" 
                              checked={q.config.required !== false} 
                              onChange={(e) => updateQuestionConfig(q.id, { required: e.target.checked })}
                              disabled={isQuestionLocked}
                              className="accent-indigo-500 disabled:opacity-50" 
                            />
                            Required
                        </label>
                      </div>
                   </div>
                 ))}
              </div>
           </div>

           {/* Releasing & monetizing the dashboard happens on the dashboard itself
               (it costs credits to publish), so it's not part of survey setup. */}
           {editingId && (
             <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 flex items-center justify-between gap-3">
               <p className="text-slate-300 text-sm flex items-center gap-2"><Database size={16} className="text-emerald-400" /> Release this survey's dashboard (free or paid, visual or JSON) from its dashboard.</p>
               <button type="button" onClick={() => navigate(`/research/campaigns/${editingId}/dashboard`)} className="text-indigo-400 hover:text-indigo-300 text-sm font-medium whitespace-nowrap">Open dashboard →</button>
             </div>
           )}

           <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-3 rounded-lg font-bold shadow-lg shadow-emerald-900/20 active:scale-95 transition-all disabled:opacity-50"
              >
                {isSaving ? <Loader2 className="animate-spin" size={20} /> : <CheckCircle2 size={20} />}
                {editingId ? 'Update Campaign' : 'Launch Campaign'}
              </button>
           </div>
        </div>
      </div>
    </div>
  );
};

export default ResearchFormView;
